import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  // Core job types
  Job,
  CreateJob,
  UpdateJob,
  CreateEnhancedJobRequest,
  UpdateEnhancedJobRequest,
  
  // Response types
  JobListResponse,
  CreateJobResponse,
  UpdateJobResponse,
  DeleteJobResponse,
  ImageUploadResponse,
  MessageResponse,
  
  // Analytics types
  JobViewRequest,
  TrackJobViewResponse,
  GetJobStatsResponse,
  GetJobAnalyticsResponse,
  
  // Bulk operations
  BulkJobStatusUpdate,
  BulkJobDelete,
  BulkUpdateJobStatusResponse,
  BulkDeleteJobsResponse,
  
  // Export types
  ExportRequest,
  ExportJobsResponse,
  
  // Utility types
  RequestConfig,
  PaginationParams,
  SearchParams,
} from './types';

/**
 * Jobs Module - Handles all job-related API operations
 * 
 * This module provides both public job endpoints (listing, search, details, view tracking)
 * and admin job management operations (CRUD, bulk operations, analytics).
 */
export class JobsModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Public Job Endpoints (Task 4.1)
  // ============================================================================

  /**
   * Get all jobs with optional search and filtering
   * GET /api/jobs
   */
  async getJobs(params?: SearchParams & PaginationParams, config?: RequestConfig): Promise<JobListResponse> {
    const queryString = params ? this.buildQueryString(params) : '';
    const endpoint = queryString ? `/api/jobs?${queryString}` : '/api/jobs';
    
    return this.get<JobListResponse>(endpoint, config);
  }

  /**
   * Get a specific job by ID
   * GET /api/jobs/:id
   */
  async getJobById(id: string, config?: RequestConfig): Promise<Job> {
    return this.get<Job>(`/api/jobs/${id}`, config);
  }

  /**
   * Get a job by ID for admin (any status including drafts)
   * GET /api/admin/jobs/:id
   */
  async getAdminJobById(id: string, config?: RequestConfig): Promise<Job> {
    return this.get<Job>(`/api/admin/jobs/${id}`, config);
  }

  /**
   * Track a job view for analytics
   * POST /api/jobs/:id/view
   */
  async trackJobView(jobId: string, viewData: JobViewRequest = {}, config?: RequestConfig): Promise<TrackJobViewResponse> {
    return this.post<TrackJobViewResponse>(`/api/jobs/${jobId}/view`, viewData, config);
  }

  /**
   * Get statistics for a specific job
   * GET /api/jobs/:id/stats
   */
  async getJobStats(jobId: string, config?: RequestConfig): Promise<GetJobStatsResponse> {
    return this.get<GetJobStatsResponse>(`/api/jobs/${jobId}/stats`, config);
  }

  // ============================================================================
  // Admin Job Management Endpoints (Task 4.2)
  // ============================================================================

  /**
   * Create a new job (Admin only)
   * POST /api/admin/jobs
   */
  async createJob(jobData: CreateJob | CreateEnhancedJobRequest, config?: RequestConfig): Promise<CreateJobResponse> {
    const transformedData = this.transformRequest(jobData);
    return this.post<CreateJobResponse>('/api/admin/jobs', transformedData, config);
  }

  /**
   * Update an existing job (Admin only)
   * PUT /api/admin/jobs/:id
   */
  async updateJob(id: string, jobData: UpdateJob | UpdateEnhancedJobRequest, config?: RequestConfig): Promise<UpdateJobResponse> {
    const transformedData = this.transformRequest(jobData);
    return this.put<UpdateJobResponse>(`/api/admin/jobs/${id}`, transformedData, config);
  }

  /**
   * Delete a job (Admin only)
   * DELETE /api/admin/jobs/:id
   */
  async deleteJob(id: string, config?: RequestConfig): Promise<DeleteJobResponse> {
    return this.delete<DeleteJobResponse>(`/api/admin/jobs/${id}`, config);
  }

  /**
   * Get job analytics (Admin only)
   * GET /api/admin/jobs/analytics
   */
  async getJobAnalytics(params?: {
    job_id?: string;
    start_date?: string;
    end_date?: string;
  }, config?: RequestConfig): Promise<GetJobAnalyticsResponse> {
    const queryString = params ? this.buildQueryString(params) : '';
    const endpoint = queryString ? `/api/admin/jobs/analytics?${queryString}` : '/api/admin/jobs/analytics';
    
    return this.get<GetJobAnalyticsResponse>(endpoint, config);
  }

  /**
   * Bulk update job status (Admin only)
   * POST /api/admin/jobs/bulk-update-status
   */
  async bulkUpdateJobStatus(request: BulkJobStatusUpdate, config?: RequestConfig): Promise<BulkUpdateJobStatusResponse> {
    return this.post<BulkUpdateJobStatusResponse>('/api/admin/jobs/bulk-update-status', request, config);
  }

  /**
   * Bulk delete jobs (Admin only)
   * POST /api/admin/jobs/bulk-delete
   */
  async bulkDeleteJobs(request: BulkJobDelete, config?: RequestConfig): Promise<BulkDeleteJobsResponse> {
    return this.post<BulkDeleteJobsResponse>('/api/admin/jobs/bulk-delete', request, config);
  }

  /**
   * Export jobs data (Admin only)
   * GET /api/admin/export/jobs
   */
  async exportJobs(request: ExportRequest, config?: RequestConfig): Promise<ExportJobsResponse> {
    const queryString = this.buildQueryString(request);
    const endpoint = `/api/admin/export/jobs?${queryString}`;
    
    return this.get<ExportJobsResponse>(endpoint, config);
  }

  /**
   * Upload a job image (company logo or job image) (Admin only)
   * POST /api/admin/jobs/upload-image
   */
  async uploadJobImage(
    file: File,
    type: 'logo' | 'job-image',
    jobId?: string,
    config?: RequestConfig
  ): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    if (jobId) {
      formData.append('job_id', jobId);
    }

    const uploadConfig: RequestConfig = {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    return this.post<ImageUploadResponse>('/api/admin/jobs/upload-image', formData, uploadConfig);
  }

  /**
   * Delete a job image (Admin only)
   * DELETE /api/admin/jobs/images/:filename
   */
  async deleteJobImage(filename: string, config?: RequestConfig): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/jobs/images/${filename}`, config);
  }

  /**
   * Generate AI image for a job (Admin only)
   * POST /api/admin/jobs/:job_id/generate-image
   */
  async generateJobImage(
    jobId: string,
    options?: {
      prompt?: string;
      style?: 'professional' | 'modern' | 'creative' | 'minimalist' | 'vibrant';
    },
    config?: RequestConfig
  ): Promise<GenerateJobImageResponse> {
    return this.post<GenerateJobImageResponse>(
      `/api/admin/jobs/${jobId}/generate-image`,
      {
        job_id: jobId,
        prompt: options?.prompt,
        style: options?.style,
      },
      config
    );
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Search jobs with specific parameters
   */
  async searchJobs(
    query?: string,
    filters?: {
      location?: string;
      job_type?: string;
      experience_level?: string;
      salary_min?: number;
      salary_max?: number;
      company?: string;
    },
    pagination?: PaginationParams,
    config?: RequestConfig
  ): Promise<JobListResponse> {
    const params: SearchParams & PaginationParams = {
      ...pagination,
    };

    if (query) {
      params.query = query;
    }

    if (filters) {
      params.filters = filters;
    }

    return this.getJobs(params, config);
  }

  /**
   * Get featured jobs (jobs with high view counts or marked as featured)
   */
  async getFeaturedJobs(limit: number = 6, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobs({
      limit,
      sort_by: 'featured',
      sort_order: 'desc'
    }, config);
  }

  /**
   * Get recent jobs
   */
  async getRecentJobs(limit: number = 10, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobs({
      limit,
      sort_by: 'created_at',
      sort_order: 'desc'
    }, config);
  }

  /**
   * Get jobs by location
   */
  async getJobsByLocation(location: string, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobs({
      filters: { location }
    }, config);
  }

  /**
   * Get jobs by company
   */
  async getJobsByCompany(company: string, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobs({
      filters: { company }
    }, config);
  }

  /**
   * Get similar jobs (based on location and other available criteria)
   */
  async getSimilarJobs(jobId: string, limit: number = 5, config?: RequestConfig): Promise<JobListResponse> {
    // First get the reference job to extract similar criteria
    const referenceJob = await this.getJobById(jobId, config);
    
    const filters: any = {};
    if (referenceJob.location) filters.location = referenceJob.location;
    // Note: Basic Job interface doesn't have job_type or experience_level
    // These would be available if using EnhancedJob interface

    return this.getJobs({
      filters,
      limit,
      sort_by: 'relevance'
    }, config);
  }

  // ============================================================================
  // Admin Convenience Methods
  // ============================================================================

  /**
   * Get all jobs for admin management
   */
  async getAdminJobs(
    filters?: {
      status?: string;
      job_type?: string;
      created_from?: string;
      created_to?: string;
    },
    pagination?: PaginationParams,
    config?: RequestConfig
  ): Promise<JobListResponse> {
    const params: SearchParams & PaginationParams = {
      ...pagination,
    };

    if (filters) {
      params.filters = filters;
    }

    // Use admin endpoint to get all jobs including drafts
    return this.getAdminJobsList(params.filters?.status || 'all', pagination, config);
  }

  /**
   * Get all jobs for admin (including drafts, archived, etc.)
   * GET /api/admin/jobs
   */
  async getAdminJobsList(
    status: string = 'all',
    pagination?: PaginationParams,
    config?: RequestConfig
  ): Promise<JobListResponse> {
    const params = new URLSearchParams();
    params.append('status', status);
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    
    return this.get<JobListResponse>(`/api/admin/jobs?${params.toString()}`, config);
  }

  /**
   * Toggle job status (activate/deactivate)
   * Note: This assumes the backend returns EnhancedJob with status field
   */
  async toggleJobStatus(jobId: string, config?: RequestConfig): Promise<UpdateJobResponse> {
    // This method assumes the job has a status field (EnhancedJob)
    // For basic Job interface, status management would be handled differently
    const updateData: UpdateEnhancedJobRequest = {
      // Toggle between active and inactive - backend should handle current state
    };
    
    return this.updateJob(jobId, updateData, config);
  }

  /**
   * Archive a job (set status to closed)
   */
  async archiveJob(jobId: string, config?: RequestConfig): Promise<UpdateJobResponse> {
    const updateData: UpdateEnhancedJobRequest = { status: 'closed' };
    return this.updateJob(jobId, updateData, config);
  }

  /**
   * Activate multiple jobs
   */
  async activateJobs(jobIds: string[], config?: RequestConfig): Promise<BulkUpdateJobStatusResponse> {
    return this.bulkUpdateJobStatus({
      job_ids: jobIds,
      status: 'active'
    }, config);
  }

  /**
   * Deactivate multiple jobs
   */
  async deactivateJobs(jobIds: string[], config?: RequestConfig): Promise<BulkUpdateJobStatusResponse> {
    return this.bulkUpdateJobStatus({
      job_ids: jobIds,
      status: 'inactive'
    }, config);
  }

  /**
   * Archive multiple jobs
   */
  async archiveJobs(jobIds: string[], config?: RequestConfig): Promise<BulkUpdateJobStatusResponse> {
    return this.bulkUpdateJobStatus({
      job_ids: jobIds,
      status: 'closed'
    }, config);
  }

  // ============================================================================
  // Analytics Convenience Methods
  // ============================================================================

  /**
   * Get job performance metrics
   */
  async getJobPerformanceMetrics(
    jobId?: string,
    dateRange?: { start_date: string; end_date: string },
    config?: RequestConfig
  ): Promise<GetJobAnalyticsResponse> {
    const params: any = {};
    
    if (jobId) params.job_id = jobId;
    if (dateRange) {
      params.start_date = dateRange.start_date;
      params.end_date = dateRange.end_date;
    }

    return this.getJobAnalytics(params, config);
  }

  /**
   * Get top performing jobs
   */
  async getTopPerformingJobs(config?: RequestConfig): Promise<GetJobAnalyticsResponse> {
    return this.getJobAnalytics({}, config);
  }

  /**
   * Track job view with additional context
   */
  async trackJobViewWithContext(
    jobId: string,
    context: {
      referrer?: string;
      user_agent?: string;
      source?: string;
    } = {},
    config?: RequestConfig
  ): Promise<TrackJobViewResponse> {
    const viewData: JobViewRequest = {
      referrer: context.referrer || document.referrer,
      user_agent: context.user_agent || navigator.userAgent,
    };

    return this.trackJobView(jobId, viewData, config);
  }

  // ============================================================================
  // Enhanced Job Management Methods (Task 13.6)
  // ============================================================================

  /**
   * Update job status
   * PATCH /api/admin/jobs/:id/status
   */
  async updateJobStatus(
    jobId: string,
    status: 'draft' | 'active' | 'archived' | 'closed',
    notes?: string,
    config?: RequestConfig
  ): Promise<Job> {
    return this.patch<Job>(`/api/admin/jobs/${jobId}/status`, { status, notes }, config);
  }

  /**
   * Toggle featured status
   * PATCH /api/admin/jobs/:id/toggle-featured
   */
  async toggleFeaturedStatus(jobId: string, config?: RequestConfig): Promise<Job> {
    return this.patch<Job>(`/api/admin/jobs/${jobId}/toggle-featured`, {}, config);
  }

  /**
   * Get featured jobs (admin version with more control)
   * GET /api/jobs?featured=true
   */
  async getAdminFeaturedJobs(config?: RequestConfig): Promise<JobListResponse> {
    return this.get<JobListResponse>('/api/jobs?featured=true', config);
  }

  /**
   * Save job as draft
   * POST /api/admin/jobs/draft
   */
  async saveJobDraft(jobData: CreateJob, config?: RequestConfig): Promise<Job> {
    const transformedData = this.transformRequest(jobData);
    return this.post<Job>('/api/admin/jobs/draft', transformedData, config);
  }

  /**
   * Load job draft
   * GET /api/admin/jobs/draft/:id
   */
  async loadJobDraft(draftId: string, config?: RequestConfig): Promise<Job> {
    return this.get<Job>(`/api/admin/jobs/draft/${draftId}`, config);
  }

  /**
   * Get job with detailed analytics
   * GET /api/admin/jobs/:id/detailed-analytics
   */
  async getJobDetailedAnalytics(jobId: string, config?: RequestConfig): Promise<any> {
    return this.get<any>(`/api/admin/jobs/${jobId}/detailed-analytics`, config);
  }

  // ============================================================================
  // Enhanced Convenience Methods
  // ============================================================================

  /**
   * Activate a job (set status to active)
   */
  async activateJob(jobId: string, config?: RequestConfig): Promise<Job> {
    return this.updateJobStatus(jobId, 'active', 'Job activated', config);
  }

  /**
   * Deactivate a job (set status to archived)
   */
  async deactivateJob(jobId: string, config?: RequestConfig): Promise<Job> {
    return this.updateJobStatus(jobId, 'archived', 'Job deactivated', config);
  }

  /**
   * Close a job (set status to closed)
   */
  async closeJob(jobId: string, config?: RequestConfig): Promise<Job> {
    return this.updateJobStatus(jobId, 'closed', 'Job closed', config);
  }

  /**
   * Mark job as featured
   */
  async markAsFeatured(jobId: string, config?: RequestConfig): Promise<Job> {
    // First check if already featured, if not, toggle
    const job = await this.getJobById(jobId, config);
    if ('is_featured' in job && !(job as any).is_featured) {
      return this.toggleFeaturedStatus(jobId, config);
    }
    return job;
  }

  /**
   * Unmark job as featured
   */
  async unmarkAsFeatured(jobId: string, config?: RequestConfig): Promise<Job> {
    // First check if featured, if yes, toggle
    const job = await this.getJobById(jobId, config);
    if ('is_featured' in job && (job as any).is_featured) {
      return this.toggleFeaturedStatus(jobId, config);
    }
    return job;
  }

  /**
   * Get jobs by status
   */
  async getJobsByStatus(
    status: 'draft' | 'active' | 'archived' | 'closed',
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<JobListResponse> {
    return this.getJobs({
      ...params,
      filters: { status }
    }, config);
  }

  /**
   * Get draft jobs
   */
  async getDraftJobs(params?: PaginationParams, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobsByStatus('draft', params, config);
  }

  /**
   * Get active jobs
   */
  async getActiveJobs(params?: PaginationParams, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobsByStatus('active', params, config);
  }

  /**
   * Get archived jobs
   */
  async getArchivedJobs(params?: PaginationParams, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobsByStatus('archived', params, config);
  }

  /**
   * Get closed jobs
   */
  async getClosedJobs(params?: PaginationParams, config?: RequestConfig): Promise<JobListResponse> {
    return this.getJobsByStatus('closed', params, config);
  }

  /**
   * Duplicate a job (create a copy)
   */
  async duplicateJob(jobId: string, config?: RequestConfig): Promise<Job> {
    const originalJob = await this.getJobById(jobId, config);
    
    // Create a new job with the same data but modified title
    const duplicateData: CreateJob = {
      ...originalJob,
      title: `${originalJob.title} (Copy)`,
      status: 'draft',
    };
    
    // Remove fields that shouldn't be copied
    delete (duplicateData as any).id;
    delete (duplicateData as any).created_at;
    delete (duplicateData as any).updated_at;
    delete (duplicateData as any).published_at;
    
    return this.createJob(duplicateData, config);
  }

  /**
   * Get job performance summary
   */
  async getJobPerformanceSummary(jobId: string, config?: RequestConfig): Promise<{
    job: Job;
    stats: GetJobStatsResponse;
    analytics: any;
  }> {
    const [job, stats, analytics] = await Promise.all([
      this.getJobById(jobId, config),
      this.getJobStats(jobId, config),
      this.getJobDetailedAnalytics(jobId, config),
    ]);

    return { job, stats, analytics };
  }

  /**
   * Publish a draft job
   */
  async publishDraft(draftId: string, config?: RequestConfig): Promise<Job> {
    // Update the draft to active status
    return this.updateJob(draftId, { status: 'active' }, config);
  }

  /**
   * Save current job as draft
   */
  async saveAsDraft(jobData: CreateJob, config?: RequestConfig): Promise<Job> {
    const draftData = {
      ...jobData,
      status: 'draft',
    };
    
    return this.saveJobDraft(draftData, config);
  }

  /**
   * Get public statistics for the home page
   * GET /api/public/stats
   */
  async getPublicStats(config?: RequestConfig): Promise<PublicStats> {
    return this.get<PublicStats>('/api/public/stats', config);
  }
}

/**
 * Public statistics response type
 */
export interface PublicStats {
  total_jobs: number;
  active_jobs: number;
  total_companies: number;
  total_placements: number;
  total_candidates: number;
  success_rate: number;
}

/**
 * Generate job image response type
 */
export interface GenerateJobImageResponse {
  success: boolean;
  image_url: string;
  message: string;
}